// ============================================================
// HARMONIC CHARTER — Song Library
// ============================================================
// Manages a multi-song library stored in localStorage.
//
// Storage layout:
//   'hchart:library'         → LibraryIndex (song metadata + folders)
//   'hchart:song:{id}'       → HChartFile (full song data)
//
// The index is lightweight — just enough to render the list.
// Full song data is loaded on demand when a chart is opened.
// ============================================================

import { v4 as uuid } from 'uuid';
import type { Song, HChartFile } from './types';

// ------------------------------------------------------------
// TYPES
// ------------------------------------------------------------

export interface SongMeta {
  id: string;
  title: string;
  updatedAt: string;
  createdAt: string;
  folderId: string | null;
  sharedWith?: string;
}

export interface Folder {
  id: string;
  name: string;
  /** Display order */
  order: number;
}

export interface LibraryIndex {
  version: '1.0';
  songs: SongMeta[];
  folders: Folder[];
}

// ------------------------------------------------------------
// STORAGE KEYS
// ------------------------------------------------------------

const INDEX_KEY  = 'hchart:library';
const SONG_KEY   = (id: string) => `hchart:song:${id}`;
const FILE_VERSION = '1.0';

// ------------------------------------------------------------
// INDEX OPERATIONS
// ------------------------------------------------------------

export function loadIndex(): LibraryIndex {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    if (raw) return JSON.parse(raw) as LibraryIndex;
  } catch {}
  return { version: '1.0', songs: [], folders: [] };
}

export function saveIndex(index: LibraryIndex): void {
  localStorage.setItem(INDEX_KEY, JSON.stringify(index));
}

// ------------------------------------------------------------
// SONG OPERATIONS
// ------------------------------------------------------------

export function loadSong(id: string): HChartFile | null {
  try {
    const raw = localStorage.getItem(SONG_KEY(id));
    if (raw) return JSON.parse(raw) as HChartFile;
  } catch {}
  return null;
}

export function saveSong(song: Song): void {
  const file: HChartFile = { version: FILE_VERSION, song };
  localStorage.setItem(SONG_KEY(song.id), JSON.stringify(file));

  // Update index metadata
  const index = loadIndex();
  const existing = index.songs.findIndex((s) => s.id === song.id);

  const meta: SongMeta = {
    id:        song.id,
    title:     song.title,
    updatedAt: song.updatedAt,
    createdAt: song.createdAt,
    folderId:  existing >= 0 ? index.songs[existing].folderId : null,
    // FIX: preserve sharedWith from the existing entry so it isn't
    // wiped on every autosave. Without this, the shared badge in
    // LibraryModal disappears after the first save cycle.
    sharedWith: existing >= 0 ? index.songs[existing].sharedWith : undefined,
  };

  if (existing >= 0) {
    index.songs[existing] = { ...index.songs[existing], ...meta };
  } else {
    index.songs.push(meta);
  }
  saveIndex(index);
}

export function deleteSong(id: string): void {
  localStorage.removeItem(SONG_KEY(id));
  const index = loadIndex();
  index.songs = index.songs.filter((s) => s.id !== id);
  saveIndex(index);
}

export function moveSongToFolder(songId: string, folderId: string | null): void {
  const index = loadIndex();
  index.songs = index.songs.map((s) =>
    s.id === songId ? { ...s, folderId } : s,
  );
  saveIndex(index);
}

// ------------------------------------------------------------
// FOLDER OPERATIONS
// ------------------------------------------------------------

export function createFolder(name: string): Folder {
  const index = loadIndex();
  const folder: Folder = {
    id:    uuid(),
    name,
    order: index.folders.length,
  };
  index.folders.push(folder);
  saveIndex(index);
  return folder;
}

export function renameFolder(id: string, name: string): void {
  const index = loadIndex();
  index.folders = index.folders.map((f) =>
    f.id === id ? { ...f, name } : f,
  );
  saveIndex(index);
}

export function deleteFolder(id: string): void {
  const index = loadIndex();
  // Move all songs in this folder to unfiled
  index.songs = index.songs.map((s) =>
    s.folderId === id ? { ...s, folderId: null } : s,
  );
  index.folders = index.folders.filter((f) => f.id !== id);
  saveIndex(index);
}

export function reorderFolders(folderIds: string[]): void {
  const index = loadIndex();
  index.folders = folderIds.map((id, order) => {
    const folder = index.folders.find((f) => f.id === id)!;
    return { ...folder, order };
  });
  saveIndex(index);
}

// ------------------------------------------------------------
// SEARCH
// ------------------------------------------------------------

export function searchSongs(
  index: LibraryIndex,
  query: string,
): SongMeta[] {
  if (!query.trim()) return index.songs;
  const q = query.toLowerCase();
  return index.songs.filter((s) =>
    s.title.toLowerCase().includes(q),
  );
}